/**
 * Compares the original data with edited data and returns an object containing only the changed fields.
 * This allows sending only modified data in form submissions.
 * Handles empty strings by normalizing them to null.
 * Ensures cleared fields (null) are included in the payload.
 * 
 * @param originalData The original data object before edits
 * @param editedData The edited data object after user changes
 * @returns An object containing only the fields that have changed
 */
export const getChangedFields = (
  originalData: Record<string, any>,
  editedData: Record<string, any>,
  debug: boolean = false
): Record<string, any> => {
  const changedFields: Record<string, any> = {};
  const normalize = (value: any) => (value === "" ? null : value);

  Object.keys(editedData).forEach(key => {
    const editedValue = normalize(editedData[key]);
    const originalValue = normalize(originalData[key]);

    const shouldInclude = editedValue !== originalValue;

    if (debug) {
      console.log(`-- KEY: ${key} --`);
      console.log(`Original:`, originalData[key]);
      console.log(`Edited:`, editedData[key]);
      console.log(`Normalized Original:`, originalValue);
      console.log(`Normalized Edited:`, editedValue);
      console.log(`Include in payload?`, shouldInclude);
    }

    if (!shouldInclude) return;

    // Nested objects
    if (
      typeof editedValue === "object" &&
      editedValue !== null &&
      typeof originalValue === "object" &&
      originalValue !== null &&
      !Array.isArray(editedValue) &&
      !Array.isArray(originalValue)
    ) {
      const nestedChanges = getChangedFields(originalValue, editedValue, debug);
      if (Object.keys(nestedChanges).length > 0) {
        changedFields[key] = nestedChanges;
      }
    } 
    // Arrays
    else if (Array.isArray(editedValue) && Array.isArray(originalValue)) {
      if (JSON.stringify(editedValue) !== JSON.stringify(originalValue)) {
        changedFields[key] = editedValue;
      }
    } 
    // Primitives or null
    else {
      changedFields[key] = editedValue;
    }
  });

  return changedFields;
};